/**
 * Mock Delivery SLA data for the B2C seller card on the desktop PDP
 * (`b2cSellerConfig.isEnabled`). The API is not wired yet, so the card is fed
 * these static raw options through the existing `mapSlaOptions` mapper — the
 * same path the multiseller flow takes with the checkDeliveryDetails (v2)
 * response, so the card renders with no mapping/presentation changes.
 *
 * TODO - Remove this mock data used (delete this file once the B2C SLA API is
 * integrated).
 */
import { PriceTypeTags, RawSlaOption, SlaCodeLabels } from './types';

/** Pincode shown in the header when the user has not entered one yet. */
export const B2C_MOCK_PINCODE = '560012';

/** Seller row under the cards. */
export const B2C_MOCK_SELLER_NAME = 'Reliance Retail';

/** Link under the seller row. No target yet — the seller list is not built. */
export const B2C_MOCK_OTHER_SELLERS_LABEL = 'Other sellers available';

/**
 * Fallback badge copy per priceType — used only when
 * `b2cSellerConfig.priceTypeTags` is absent from the flag payload.
 */
export const B2C_MOCK_PRICE_TYPE_TAGS: PriceTypeTags = {
  best: 'BEST',
  fastest: 'FASTEST'
};

/**
 * SLA-code → copy for the express row. `2026-07-31` is a Friday, so the
 * configured copy names that day the way the multiseller day-bucket codes do.
 */
export const B2C_MOCK_SLA_CODE_LABELS: SlaCodeLabels = {
  '4HR': '4 hours, Fri'
};

/** Heading of the seller-list modal opened from "Other sellers available". */
export const B2C_MOCK_MODAL_TITLE = 'Delivery Detail';

const MOCK_PRODUCT_CODE = 'B2C_MOCK_SKU';

export const B2C_MOCK_SLA_OPTIONS: RawSlaOption[] = [
  {
    priceType: 'best',
    isSelected: true,
    priceGroupId: 'b2c-mock-best',
    price: {
      priceData: { displayformattedValue: '₹1,00,650', discountPercent: '70% off' },
      wasPriceData: { displayformattedValue: '₹2,03,599' }
    },
    serviceabilityDetails: {
      serviceability: true,
      pinCode: B2C_MOCK_PINCODE,
      codEligible: true,
      productDetails: [
        {
          productCode: MOCK_PRODUCT_CODE,
          serviceability: true,
          deliveryMethod: 'STANDARD',
          eddUpper: '2026-07-28T18:30:00',
          deliveryFee: '0',
          codEligible: true
        }
      ]
    },
    stock: { stockLevelStatus: 'inStock', stockLevel: 10 }
  },
  {
    priceType: 'fastest',
    isSelected: false,
    priceGroupId: 'b2c-mock-fastest',
    price: {
      priceData: { displayformattedValue: '₹2,03,599' }
    },
    serviceabilityDetails: {
      serviceability: true,
      pinCode: B2C_MOCK_PINCODE,
      codEligible: true,
      productDetails: [
        {
          productCode: MOCK_PRODUCT_CODE,
          serviceability: true,
          deliveryMethod: 'RUSH',
          deliverySLA: '4HR',
          eddUpper: '2026-07-31T18:30:00',
          cutOffTime: '2026-07-31T21:00:00',
          deliveryFee: '0',
          codEligible: true
        }
      ]
    },
    stock: { stockLevelStatus: 'inStock', stockLevel: 5 }
  }
];

/* ------------------------------------------------------------------ */
/* Size/Quantity popup (desktop cart + wishlist)                       */
/* ------------------------------------------------------------------ */

/**
 * Seller row inside the Size/Quantity popup. The design names the full legal
 * entity here, unlike the PDP card.
 *
 * TODO - Remove this mock data used.
 */
export const B2C_MOCK_POPUP_SELLER_NAME = 'Fashion factory Reliance Retail Pvt Ltd';

/**
 * Cards for the Size/Quantity popup — a priced STANDARD row and a priced RUSH
 * row, so both badges (BEST PRICE / FASTEST) and the radio selection show up the
 * way the design has them. Same raw shape as the PDP mock, so it runs through
 * `mapSlaOptions` untouched.
 *
 * TODO - Remove this mock data used.
 */
export const B2C_MOCK_POPUP_SLA_OPTIONS: RawSlaOption[] = [
  {
    priceType: 'best',
    isSelected: true,
    priceGroupId: 'b2c-mock-popup-best',
    price: {
      priceData: { displayformattedValue: '₹1,00,650', discountPercent: '70% off' },
      wasPriceData: { displayformattedValue: '₹1,03,599' }
    },
    serviceabilityDetails: {
      serviceability: true,
      pinCode: B2C_MOCK_PINCODE,
      codEligible: true,
      productDetails: [
        {
          productCode: MOCK_PRODUCT_CODE,
          serviceability: true,
          deliveryMethod: 'STANDARD',
          eddUpper: '2026-07-28T18:30:00',
          deliveryFee: '0',
          codEligible: true
        }
      ]
    },
    stock: { stockLevelStatus: 'inStock', stockLevel: 10 }
  },
  {
    priceType: 'fastest',
    isSelected: false,
    priceGroupId: 'b2c-mock-popup-fastest',
    price: {
      priceData: { displayformattedValue: '₹1,00,650', discountPercent: '70% off' },
      wasPriceData: { displayformattedValue: '₹2,03,599' }
    },
    serviceabilityDetails: {
      serviceability: true,
      pinCode: B2C_MOCK_PINCODE,
      codEligible: true,
      productDetails: [
        {
          productCode: MOCK_PRODUCT_CODE,
          serviceability: true,
          deliveryMethod: 'RUSH',
          deliverySLA: '4HR',
          eddUpper: '2026-07-31T18:30:00',
          cutOffTime: '2026-07-31T21:00:00',
          deliveryFee: '0',
          codEligible: true
        }
      ]
    },
    stock: { stockLevelStatus: 'inStock', stockLevel: 5 }
  }
];


/* ---- desktop cart tile ---- */

/** Seller named under the price on the cart tile. */
export const B2C_MOCK_CART_SELLER_NAME = 'Reliance Trends';

/** Badge to the left of the strip copy. `rush` is the shared RUSH pill asset. */
export type B2cCartStripBadge = 'rush' | 'price';

export interface B2cCartStrip {
  badge?: B2cCartStripBadge;
  note: string;
  cta: string;
}

/**
 * "Compare sellers" strips, one per cart line in order. Which one a line really
 * gets is the API's call — a faster option, or a cheaper one — so until that
 * lands the tile picks by position and wraps round past the end.
 */
export const B2C_MOCK_CART_STRIPS: B2cCartStrip[] = [
  { badge: 'rush', note: 'Delivery in 60mins!', cta: 'Compare' },
  { note: 'Faster delivery by Today!', cta: 'Compare' },
  { badge: 'price', note: 'Better price available!', cta: 'Compare' }
];
